import { createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../core/api/api';
import * as appServiceLines from '../core/service-lines';
import * as cmxDateTime from '../core/date-time/cmx-date-time';
import * as commonActions from './action-types';
import * as configurationUtils from '../core/configuration-utils';
import * as Utils from '../core/artifacts/utils';

const { Bench } = commonActions;

const REQUEST_INIT_OPTIONS: RequestInit = {
  method: 'GET',
  cache: 'no-cache',
  redirect: 'follow',
  referrer: 'no-referrer'
};

/**
 * Function that returns the current process instance.
 */
const getProcessInstance = async url => {
  const processInstance = await api.requestJSON<AmplifyAPI.ProcessInstance>(url, {
    ...REQUEST_INIT_OPTIONS
  });

  return processInstance;
};

const fetchActivity = createAsyncThunk<
  (CMxAPI.UserActivity | CMxAPI.SystemActivity | CMxAPI.UserActivityComment)[],
  AmplifyAPI.ProcessInstance,
  { rejectValue: any }
>(Bench.ACTIVITY, async (processInstance) => {
  const serviceLineConfig = appServiceLines.determineServiceLineConfig({
    name: processInstance.serviceLine
  });
  const userActivityPromise = api.requestJSON<CMxAPI.UserActivity[]>(
    `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${processInstance.guid.uuid}/useractivity/v1`,
    { ...REQUEST_INIT_OPTIONS }
  );
  const systemActivityPromise = api.requestJSON<CMxAPI.SystemActivity[]>(
    `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${processInstance.guid.uuid}/systemactivity/v1`,
    { ...REQUEST_INIT_OPTIONS }
  );

  const userActivityCommentsPromise = api.requestJSON<CMxAPI.UserActivityComment[]>(
    `/coding-api/processinstance/${serviceLineConfig.amplifyPath}/${processInstance.guid.uuid}/useractivity/comments/v1`,
    { ...REQUEST_INIT_OPTIONS }
  );

  const [userActivity, systemActivity, userActivityComments] = await Promise.all([
    userActivityPromise,
    systemActivityPromise,
    userActivityCommentsPromise
  ]);
  const activity = [
    ...userActivity.map(activity => ({ ...activity, activityType: 'USER' })),
    ...systemActivity.map(activity => ({ ...activity, activityType: 'SYSTEM' })),
    ...userActivityComments.map(activity => ({
      ...activity,
      activityType: 'USER_ACTIVITY_COMMENTS'
    }))
  ];
  activity.sort((a, b) =>
    cmxDateTime.sortByDate(a.timestamp, b.timestamp)
  );
  return activity;
});

const updateProcessInstance = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  CMxCommonApp.ProcessInstanceParams,
  { rejectValue: CMxCommonApp.CMxError }
>(Bench.UPDATE_PROCESS_INSTANCE, async (processInstanceParam, { dispatch }) => {
  const processInstance = await getProcessInstance(
    `/coding-api/processinstance/${processInstanceParam.serviceLine}/${processInstanceParam.guid}/v1?evidence=true&errors=true`
  );
  dispatch(fetchActivity(processInstance) as any);
  return processInstance;
});

const fetchCodeBenchLayoutConfiguration = createAsyncThunk<
  any,
  { tenantId: string; serviceLine: string },
  { rejectValue: CMxCommonApp.CMxError }
>(
  'FETCH_CODE_BENCH_LAYOUT_CONFIGURATION',
  async (params, { getState }) => {
    const { tenantId, serviceLine } = params;
    const state = getState() as any;

    // @ts-ignore state.ui exists on the newer (non-embedded) application V2 UI wont be supported on the embedded application.
    if (state?.ui?.hasAmplifyCaseRunV2Access && Utils.doesPathEndWithV2()) {
      return configurationUtils.fetchMergedConfigurationJsonValue(
        'demographics-bench-layout',
        tenantId,
        serviceLine
      );
    }

    return configurationUtils.fetchMergedConfigurationJsonValue(
      'code-bench-layout',
      tenantId,
      serviceLine
    );
  }
);

export {
  fetchActivity,
  fetchCodeBenchLayoutConfiguration,
  getProcessInstance,
  updateProcessInstance
};
